import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGlobalState } from '../contexts/GlobalContext';
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaBriefcase,
  FaFacebook,
  FaLinkedin,
  FaTwitter,
} from 'react-icons/fa';
import LoadingSpinner from '../Components/LoadingSpinner';
import { apiFetch } from '../utils/api';
import { isRealtor } from '../utils/userAccess';

const ACTIVE_LISTING_STATUSES = new Set(['active']);

const normalizeMoney = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return numeric.toLocaleString();
};

const normalizeStellarListing = (listing) => {
  const media = Array.isArray(listing?.Media)
    ? listing.Media
    : Array.isArray(listing?.media)
      ? listing.media
      : [];
  const firstMedia = media[0] || {};
  const streetAddressParts = [
    listing.StreetNumber,
    listing.StreetDirPrefix,
    listing.StreetName,
    listing.StreetSuffix,
    listing.StreetSuffixModifier,
    listing.UnitNumber ? `#${listing.UnitNumber}` : null,
  ].filter(Boolean);
  const derivedStreetAddress = streetAddressParts.join(' ').trim();

  return {
    listingId:
      listing.ListingKeyNumeric ||
      listing.ListingKey ||
      listing.ListingId ||
      listing.listing_id ||
      listing.id,
    propertyId:
      listing.PropertyKeyNumeric ||
      listing.PropertyKey ||
      listing.ListingKeyNumeric ||
      listing.ListingKey ||
      listing.property_id ||
      listing.id,
    imageUrl:
      firstMedia.MediaURL ||
      firstMedia.MediaURLLarge ||
      firstMedia.Uri800 ||
      firstMedia.Uri ||
      firstMedia.URL ||
      listing.MediaURL ||
      '',
    streetAddress:
      listing.UnparsedAddress ||
      derivedStreetAddress ||
      listing.street_address ||
      'Address unavailable',
    city: listing.City || listing.city || '',
    state: listing.StateOrProvince || listing.state || '',
    zipCode: listing.PostalCode || listing.zip_code || '',
    listPrice: normalizeMoney(listing.ListPrice ?? listing.list_price),
    listingStatus:
      listing.StandardStatus ||
      listing.MlsStatus ||
      listing.ListingStatus ||
      listing.status ||
      '',
    bedrooms:
      listing.BedroomsTotal ??
      listing.BedroomsPossible ??
      listing.bedrooms ??
      null,
    bathrooms:
      listing.BathroomsTotalInteger ??
      listing.BathroomsFull ??
      listing.bathrooms ??
      null,
    acreage:
      listing.LotSizeAcres ??
      listing.lot_acres ??
      listing.acreage ??
      null,
    livingSqFt:
      listing.LivingArea ??
      listing.BuildingAreaTotal ??
      listing.living_sqft ??
      null,
    totalSqFt:
      listing.BuildingAreaTotal ??
      listing.total_sqft ??
      null,
    yearBuilt:
      listing.YearBuilt ??
      listing.year_built ??
      null,
    zoning:
      listing.ZoningDescription ??
      listing.Zoning ??
      listing.zoning ??
      null,
    parcelNumber:
      listing.ParcelNumber ??
      listing.TaxParcelLetter ??
      listing.parcel_number ??
      null,
    propertySubtype:
      listing.PropertySubType ??
      listing.PropertyType ??
      listing.property_subtype ??
      null,
    propertyType:
      listing.PropertyType ??
      listing.property_type ??
      null,
    mls_id:
      listing.ListingId ??
      listing.MLSNumber ??
      listing.mls_id ??
      null,
    source: 'stellar',
    sourceUrl:
      listing.STELLAR_DPRURL ||
      listing.VirtualTourURLUnbranded ||
      listing.VirtualTourURLBranded ||
      listing.url ||
      null,
    listingLink:
      listing.STELLAR_DPRURL ||
      listing.VirtualTourURLUnbranded ||
      listing.VirtualTourURLBranded ||
      listing.url ||
      null,
  };
};

function Profile() {
  const { userId } = useParams();
  const { state, fetchListings } = useGlobalState();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [yearsExperience, setYearsExperience] = useState(null);
  const [stellarListings, setStellarListings] = useState([]);
  const [loadingStellarListings, setLoadingStellarListings] = useState(false);

  const realtorProfile = isRealtor(profile);
  const brokerageName = profile
    ? typeof profile.brokerage === 'object'
      ? profile.brokerage?.officeBusinessName ||
        profile.brokerage?.office_business_name ||
        ''
      : profile.brokerageName || profile.brokerage || ''
    : '';
  const titleLine = [profile?.title, realtorProfile ? brokerageName : null]
    .filter(Boolean)
    .join(' at ');
  const specializations = Array.isArray(profile?.specializations)
    ? profile.specializations.filter(Boolean)
    : [];
  const socialLinks = profile?.socialLinks || {};
  const hasSocialLinks = Boolean(
    socialLinks.facebook || socialLinks.linkedin || socialLinks.x
  );
  const activeListings =
    realtorProfile && profile
      ? state.listings.filter(
          (listing) =>
            String(listing.creatorID) === String(profile.id) &&
            ACTIVE_LISTING_STATUSES.has(String(listing.listingStatus || '').trim().toLowerCase())
        )
      : [];
  const hasStellarMembership = Boolean(profile?.memberMlsId);
  const activeStellarListings = stellarListings.filter((listing) =>
    ACTIVE_LISTING_STATUSES.has(String(listing.listingStatus || '').trim().toLowerCase())
  );

  const renderListingCard = (listing) => {
    const card = (
      <div
        className="card shadow-sm p-2"
        style={{ fontSize: '0.8rem' }}
      >
        <img
          src={listing.imageUrl || 'https://via.placeholder.com/100'}
          className="card-img-top"
          alt={listing.streetAddress}
          style={{
            height: '100px',
            objectFit: 'cover',
            borderRadius: '5px',
          }}
        />
        <div className="card-body p-2 text-center">
          <h6 className="card-title mb-1">
            {listing.streetAddress}
          </h6>
          <p className="text-muted mb-1">
            {[listing.city, listing.state].filter(Boolean).join(', ')}
            {listing.zipCode ? ` ${listing.zipCode}` : ''}
          </p>
          <p className="text-primary mb-1">
            <strong>{listing.listPrice ? `$${listing.listPrice}` : '—'}</strong>
          </p>
          {listing.listingStatus && (
            <p className="text-muted mb-1">{listing.listingStatus}</p>
          )}

          <div className="row g-0">
            <div className="col-4">
              <p className="mb-0">
                <strong>{listing.bedrooms || '—'} 🛏️</strong>
              </p>
            </div>
            <div className="col-4">
              <p className="mb-0">
                <strong>{listing.bathrooms || '—'} 🛁</strong>
              </p>
            </div>
            <div className="col-4">
              <p className="mb-0">
                <strong>{listing.livingSqFt || '—'} sqft</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    );

    if (listing.source === 'stellar') {
      return (
        <Link
          to={`/property/stellar-${listing.listingId}`}
          state={{ stellarListing: listing }}
          className="text-decoration-none text-dark"
        >
          {card}
        </Link>
      );
    }

    return (
      <Link
        to={`/property/${listing.propertyId}`}
        className="text-decoration-none text-dark"
      >
        {card}
      </Link>
    );
  };

  useEffect(() => {
    if (realtorProfile && state.listings.length === 0) {
      fetchListings();
    }
  }, [realtorProfile, state.listings.length, fetchListings]);

  useEffect(() => {
    let cancelled = false;

    const loadStellarListings = async () => {
      if (!realtorProfile || !profile?.memberMlsId) {
        if (!cancelled) {
          setStellarListings([]);
          setLoadingStellarListings(false);
        }
        return;
      }

      setLoadingStellarListings(true);
      try {
        const result = await apiFetch('fetchStellarMemberListings', {
          queryParams: { member_mls_id: profile.memberMlsId },
        });
        const rawListings = Array.isArray(result?.listings) ? result.listings : [];
        const normalized = rawListings
          .map(normalizeStellarListing)
          .filter((listing) => listing.listingId);

        if (!cancelled) {
          setStellarListings(normalized);
        }
      } catch (error) {
        if (!cancelled) {
          setStellarListings([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingStellarListings(false);
        }
      }
    };

    loadStellarListings();

    return () => {
      cancelled = true;
    };
  }, [profile?.memberMlsId, realtorProfile]);

  useEffect(() => {
    let cancelled = false;

    const applyProfile = (nextProfile) => {
      if (cancelled) return;

      setProfile(nextProfile);

      if (nextProfile?.startDate) {
        const startYear = new Date(nextProfile.startDate).getFullYear();
        const currentYear = new Date().getFullYear();
        setYearsExperience(currentYear - startYear);
      } else {
        setYearsExperience(null);
      }
    };

    const loadProfile = async () => {
      setLoadingProfile(true);
      setProfileError(null);

      const foundUser = state.users?.find(
        (user) => String(user.userId) === String(userId)
      );

      if (foundUser) {
        applyProfile(foundUser);
        if (!cancelled) {
          setLoadingProfile(false);
        }
        return;
      }

      try {
        const result = await apiFetch('fetchProfile', {
          queryParams: { user_id: userId },
        });
        applyProfile(result.user);
      } catch (err) {
        if (!cancelled) {
          setProfileError(err.message || 'Unable to load profile.');
          setProfile(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingProfile(false);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [userId, state.users]);

  if (loadingProfile) {
    return (
      <div className="container mt-4">
        <LoadingSpinner message="Loading profile..." />
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger mb-0">{profileError}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning mb-0">Profile not found.</div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="card shadow-sm p-4">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <img
              src={profile.profilePicture || 'https://placehold.co/100'}
              alt="Profile"
              className="rounded-circle me-3"
              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
            />
            <div>
              <h3 className="mb-1">{profile.name}</h3>
              {titleLine && <p className="text-muted mb-1">{titleLine}</p>}
              {!titleLine && profile.profession && (
                <p className="text-muted mb-1 text-capitalize">
                  {profile.profession}
                </p>
              )}

              {realtorProfile && yearsExperience !== null && (
                <p className="text-muted mb-0">
                  <strong>Experience:</strong> • {yearsExperience}{' '}
                  {yearsExperience === 1 ? 'Year' : 'Years'}
                </p>
              )}
            </div>
          </div>

          {String(state.user?.userId) === String(profile.userId) && (
            <Link
              to={`/edit-profile/${profile.userId}`}
              className="btn btn-outline-primary btn-sm"
            >
              Edit Profile
            </Link>
          )}
        </div>

        <div className="mt-3">
          {profile.cellPhone && (
            <p>
              <FaPhone className="me-2 text-primary" /> {profile.cellPhone}
            </p>
          )}
          {profile.email && (
            <p>
              <FaEnvelope className="me-2 text-primary" /> {profile.email}
            </p>
          )}
          {profile.location && (
            <p>
              <FaMapMarkerAlt className="me-2 text-primary" /> {profile.location}
            </p>
          )}
        </div>

        {specializations.length > 0 && (
          <div className="mt-3">
            <h5>{realtorProfile ? 'Specializations' : 'Focus Areas'}</h5>
            <ul className="list-group">
              {specializations.map((specialty, index) => (
                <li key={`${specialty}-${index}`} className="list-group-item">
                  <FaBriefcase className="me-2 text-success" /> {specialty}
                </li>
              ))}
            </ul>
          </div>
        )}

        {profile.bio && (
          <div className="mt-3">
            <h5>About Me</h5>
            <p className="text-muted">{profile.bio}</p>
          </div>
        )}

        {realtorProfile && (
          <div className="mt-3">
            <h5>Active Listings</h5>
            {activeListings.length > 0 ? (
                <div className="row row-cols-2 row-cols-md-4 row-cols-lg-6 g-3">
                  {activeListings.map((listing) => (
                    <div key={`app-${listing.listingId}`} className="col">
                      {renderListingCard(listing)}
                    </div>
                  ))}
                </div>
            ) : (
              <p className="text-muted">No active listings yet.</p>
            )}
          </div>
        )}

        {realtorProfile && hasStellarMembership && (
          <div className="mt-4">
            <h5>Stellar MLS Listings</h5>
            {loadingStellarListings ? (
              <p className="text-muted mb-0">Loading Stellar MLS listings...</p>
            ) : activeStellarListings.length > 0 ? (
              <div className="row row-cols-2 row-cols-md-4 row-cols-lg-6 g-3">
                {activeStellarListings.map((listing) => (
                  <div key={`stellar-${listing.listingId}`} className="col">
                    {renderListingCard(listing)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted">No Stellar MLS listings found for this member.</p>
            )}
          </div>
        )}

        {hasSocialLinks && (
          <div className="mt-3 text-center">
            <h5>Connect with Me</h5>
            {socialLinks.facebook && (
              <a
                href={`https://facebook.com/${socialLinks.facebook}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary me-2"
              >
                <FaFacebook />
              </a>
            )}
            {socialLinks.x && (
              <a
                href={`https://x.com/${socialLinks.x}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary me-2"
              >
                <FaTwitter />
              </a>
            )}
            {socialLinks.linkedin && (
              <a
                href={`https://linkedin.com/in/${socialLinks.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary me-2"
              >
                <FaLinkedin />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
